import {
  Box,
  Checkbox,
  HStack,
  System,
  SystemProps,
  Text,
  VStack,
  system,
} from "flicket-ui";
import { useEffect, useState } from "react";
import { Icon } from "~components";
import { CaretRight, Flag } from "@phosphor-icons/react";
import styled, { css } from "styled-components";
import { useIsMounted, useOrganization } from "~hooks";
import { OrganizationFeatures } from "~lib/features";

import { useSessionStorage } from "usehooks-ts";
import { useAvailableFeatureFlags } from "./useAvailableFeatureFlags";
import { FeatureStatusEnum, AvailableFeaturesQuery } from "~graphql/sdk";
import { caseInsensitiveIncludes } from "~lib/caseInsensitiveIncludes";

const featureStatusColors: Record<FeatureStatusEnum, SystemProps["color"]> = {
  [FeatureStatusEnum.Beta]: "blue.base",
  [FeatureStatusEnum.Development]: "error",
  [FeatureStatusEnum.Released]: "success",
  [FeatureStatusEnum.Deprecated]: "warning",
};

const ItemCheckbox = styled(Checkbox)`
  && {
    &:checked {
      border-color: black;
    }

    &:before {
      background-color: black;
    }
  }
`;

const Li = styled.li<SystemProps<"li">>`
  ${system}
`;

const FeatureDescription = ({
  feature: { description, status },
}: {
  feature: AvailableFeaturesQuery["availableFeatures"]["features"][0];
}) => (
  <VStack>
    <Text variant="small">{description}</Text>
    <HStack gap={"1/2"}>
      <Text variant="small">Status:</Text>

      <Text variant="small" color={featureStatusColors[status]}>
        {status}
      </Text>
    </HStack>
  </VStack>
);

export type FeatureFlag = OrganizationFeatures;
export type SessionFeatureFlagType =
  | Record<FeatureFlag, boolean>
  | Record<never, never>;

export default function FeatureFlagPanel() {
  const { organization } = useOrganization();

  const {
    featureFlags,
    addFeatureFlags,
    toggleFeatureFlag,
  } = useSessionFeatureFlags();
  const availableFeatureFlags = useAvailableFeatureFlags();

  const [isOpen, setIsOpen] = useState(false);

  const isMounted = useIsMounted();

  function togglePanelOpen() {
    setIsOpen(!isOpen);
  }

  useEffect(() => {
    // When the organization loads, store all feature flags into the session storage
    if (organization) {
      const features: SessionFeatureFlagType = Object.values(
        OrganizationFeatures
      )
        .sort((a, b) => a.localeCompare(b))
        .reduce((acc, feature) => {
          acc[feature] =
            (featureFlags[feature] ||
              caseInsensitiveIncludes(organization.features, feature)) ??
            false;
          return acc;
        }, {});

      addFeatureFlags(features);
    }
  }, [organization]);

  return (
    <>
      <Box
        css={css((p) => ({
          position: "fixed",
          right: isOpen ? 0 : -280,
          top: 0,
          width: 280,
          bottom: 0,
          backgroundColor: p.theme.colors.N200,
          zIndex: 999999,
          boxShadow: p.theme.shadows.hover,
        }))}
      >
        <Box
          css={css((p) => ({
            position: "absolute",
            left: -30,
            top: 0,
            width: 30,
            height: 30,
            cursor: "pointer",
            backgroundColor: p.theme.colors.N200,
          }))}
          onClick={togglePanelOpen}
        >
          <Icon
            icon={
              isOpen ? (
                <CaretRight size={30} weight="regular" />
              ) : (
                <Flag size={30} weight="duotone" />
              )
            }
          />
        </Box>{" "}
        <Box mb={2} overflowY="auto" pb={8} height="100%">
          <Box p={2}>
            <Text variant="header.S">Feature Flags</Text>
            <Text variant="regular" mb={2}>
              These changes only effect your current browser tab session. You
              may need to refresh the page if you do not see changes.
            </Text>
          </Box>
          <ul>
            {isMounted &&
              Object.entries(availableFeatureFlags).map(([, feature]) => (
                <Li key={feature.key} pl={"16px" as any} listStyleType="none">
                  <ItemCheckbox
                    label={feature.label}
                    description={<FeatureDescription feature={feature} />}
                    checked={!!featureFlags[feature.key]}
                    onChange={() =>
                      toggleFeatureFlag(feature.key, !featureFlags[feature.key])
                    }
                  />
                </Li>
              ))}
          </ul>
        </Box>
      </Box>
    </>
  );
}

export function useSessionFeatureFlags() {
  const [value, setValue] = useSessionStorage<SessionFeatureFlagType>(
    "features",
    {}
  );

  function toggleFeatureFlag(feature: FeatureFlag, value: boolean) {
    setValue((state) => ({
      ...state,
      [feature]: value,
    }));
  }

  function addFeatureFlags(features: SessionFeatureFlagType) {
    setValue((state) => ({
      ...state,
      ...features,
    }));
  }

  return {
    featureFlags: value,
    addFeatureFlags,
    toggleFeatureFlag,
  };
}
